<template>
  <div class="details-panel">
    <ZoomCenterTransition>
      <TriggerButton
        v-if="!showPanel"
        @click="showPanel = true"
        icon="bar_chart"
      />
    </ZoomCenterTransition>
    <SlideXRightTransition>
      <div v-if="showPanel" class="panel smooth-shadow">
        <div class="panel-header smooth-shadow">
          <Spinner v-if="isRequestInProgress" small />
          <icon v-else size="18">bar_chart</icon>
          <h3 class="mar-left-10">Layer Analysis</h3>
          <expand />
          <icon type="button" size="18" @click="showPanel = false">clear</icon>
        </div>
        <simplebar class="panel-body-wrapper" data-auto-hide="true">
          <div class="panel-body">
            <div v-if="getDetailsWidget" class="metrics-card">
              <div class="header">
                <div class="header-texts">
                  <h4 class="title ellipsis">
                    {{ getActiveLayerDetails.name }}
                  </h4>
                  <p class="description">Change since last period</p>
                </div>
                <div class="values">
                  <p class="value">{{ getEventDetails.current }}</p>
                  <span
                    :class="[
                      'percentage',
                      { decreasing: !getEventDetails.isIncreasing },
                    ]"
                    >{{ getEventDetails.change }}%</span
                  >
                </div>
              </div>

              <div class="header">
                <div class="flex-col flex-expand">
                  <h4 class="title ellipsis">Total Entities</h4>
                  <div class="values">
                    <div class="flex-col">
                      <p class="value">{{ getEntitiesDetails.current }}</p>
                      <span
                        :class="[
                          'percentage',
                          { decreasing: !getEntitiesDetails.isIncreasing },
                        ]"
                        >{{ getEntitiesDetails.change }}%</span
                      >
                    </div>
                  </div>
                </div>
                <div class="flex-col flex-expand">
                  <h4 class="title ellipsis">Total Sessions</h4>
                  <div class="values">
                    <div class="flex-col">
                      <p class="value">{{ getSessionDetails.current }}</p>
                      <span
                        :class="[
                          'percentage',
                          { decreasing: !getSessionDetails.isIncreasing },
                        ]"
                        >{{ getSessionDetails.change }}%</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DistributionCard
              v-if="getDistributionSeries"
              :series="getDistributionSeries"
            />
          </div>
        </simplebar>
      </div>
    </SlideXRightTransition>
  </div>
</template>

<script>
import { ZoomCenterTransition, SlideXRightTransition } from "vue2-transitions";
import TriggerButton from "@/components/static_entity_analysis/views/TriggerButton";
import { mapGetters } from "vuex";
import { format } from "@/lib/numberFormatter";
import DistributionCard from "@/components/static_entity_analysis/views/graphs/distribution";

export default {
  components: {
    ZoomCenterTransition,
    SlideXRightTransition,
    TriggerButton,
    DistributionCard,
  },
  data() {
    return {
      showPanel: true,
    };
  },
  computed: {
    ...mapGetters("StaticEntity", [
      "getDetailsWidget",
      "getActiveLayerDetails",
      "getDistributions",
    ]),
    ...mapGetters("Data", ["isRequestInProgress"]),
    getEventDetails() {
      const data = this.getDetailsWidget ? this.getDetailsWidget.data : null;
      if (!(data && data.event_count && data.event_count.current))
        return {
          current: 0,
          change: 0,
        };
      return this.calculateIncrease(data.event_count);
    },
    getSessionDetails() {
      const data = this.getDetailsWidget ? this.getDetailsWidget.data : null;
      if (!(data && data.entities_count && data.entities_count.current))
        return {
          current: 0,
          change: 0,
        };

      return this.calculateIncrease(data.entities_count);
    },
    getEntitiesDetails() {
      const data = this.getDetailsWidget ? this.getDetailsWidget.data : null;
      if (!(data && data.sessions_count && data.sessions_count.current))
        return {
          current: 0,
          change: 0,
        };

      return this.calculateIncrease(data.sessions_count);
    },
    getDistributionSeries() {
      if (!this.getDistributions) return null;

      return Object.keys(this.getDistributions).map(key => {
        return {
          name: key,
          xtitle: key === "daily" ? "Hours" : key === "weekly" ? "Days" : "",
          data: this.getDistributions[key],
        };
      });
    },
  },
  methods: {
    formatNumber(num) {
      let value = parseFloat(num || 0);
      value = isNaN(value) ? 0 : value;
      value = format(value.toFixed(2), 2);
      return value;
    },
    calculateIncrease({ current, previous }) {
      const isIncreasing = current > previous ? true : false;
      return {
        current: this.formatNumber(current),
        change: this.formatNumber(
          isIncreasing ? (previous / current) * 100 : (current / previous) * 100
        ),
        isIncreasing,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.details-panel {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 99;

  .trigger-button {
    padding: 10px;
    background: var(--color-secondary);
    border-radius: 50%;
    transition: 0.1s all ease-in-out;

    &:hover {
      color: var(--color-primary);
      cursor: pointer;
    }

    &:active {
      transform: scale(0.9);
    }
  }

  .panel {
    width: 300px;
    height: 55vh;
    background: var(--color-default-background);
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    border-radius: 5px;

    .panel-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      border-radius: 5px 5px 0 0;
      background: var(--color-secondary);
      padding: 7px 5px 7px 15px;

      .header-texts {
        display: flex;
        flex: 1;
      }
    }

    .panel-body-wrapper {
      /* THIS WORKS: */
      flex: 1;
      min-height: 5px;

      .panel-body {
        /* overflow: auto; */

        .no-content-image {
          display: flex;
          flex-direction: column;
          padding: 15px;
          justify-content: center;
          align-items: center;

          .image {
            height: 200px;
            width: auto;
          }
          p {
            margin: 0;
            opacity: 0.7;
            margin-top: 20px;
            text-align: center;
          }
        }
      }
    }

    .metrics-card {
      display: flex;
      flex-direction: column;
      background: var(--color-secondary);
      border-radius: 5px;
      margin: 10px 10px;

      .header {
        display: flex;
        flex-direction: row;
        flex: 1;
        padding: 10px;
        border-bottom: 1px solid var(--border-color);

        .header-texts {
          display: flex;
          flex: 1;
          flex-direction: column;
          justify-content: center;
        }

        &:last-child {
          border-bottom: 0;
        }

        h4 {
          font-size: 1em;
        }
        p {
          margin: 0;
          padding: 0;
        }

        .description {
          font-size: 0.8;
          opacity: 0.7;
        }
      }

      .values {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0px 0;

        .value {
          font-size: 1.3em;
          margin-right: 10px;
          padding: 10px 0 2px 0;
        }

        label {
          font-size: 0.7em;
        }

        .percentage {
          color: #16f559;

          &::before {
            content: "+";
          }

          &.decreasing {
            color: #f84b20;
            &::before {
              content: "-";
            }
          }
        }
      }

      // .metric-area {
      //   display: flex;
      //   flex-
      // }
    }
  }
}
</style>
